import React from 'react';
import { Box, Container, Typography, Paper, Link } from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Box
      sx={{
        backgroundColor: '#f5f7fb',
        minHeight: '100vh',
        pb: 4,
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <Box
        component="header"
        sx={{ backgroundColor: '#007f8b', color: '#fff', py: 4, textAlign: 'center' }}
      >
        <Typography variant="h1" sx={{ fontSize: '30px', fontWeight: 'bold', m: 0 }}>
          Terms and Conditions
        </Typography>
        <Typography variant="subtitle1">UTrust Platform — Australia &amp; Western Australia Compliance Version</Typography>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <Typography paragraph>
            <strong>Effective Date:</strong> 13/05/2026
          </Typography>
          <Typography paragraph>
            <strong>Version:</strong> 1.0
          </Typography>
          <Typography paragraph>
            <strong>Platform:</strong> UTrust — Ubumwe Trust Platforms
          </Typography>

          {/* Section 1 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            1. Agreement to Terms
          </Typography>
          <Typography paragraph>
            These Terms and Conditions ("Terms") govern access to and use of the UTrust platform,
            website, mobile application, software, and associated services ("Platform").
          </Typography>
          <Typography paragraph>
            By accessing, registering for, or using the Platform, you agree to be legally bound by
            these Terms.
          </Typography>
          <Typography paragraph>
            If you do not agree to these Terms, you must immediately stop using the Platform.
          </Typography>
          <Typography paragraph>
            These Terms are governed by the laws of Western Australia and the Commonwealth of
            Australia.
          </Typography>

          {/* Section 2 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            2. Definitions
          </Typography>
          <Typography paragraph>In these Terms:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>"UTrust", "we", "our", or "us" refers to the operators of the UTrust platform.</li>
            <li>"User" refers to any individual or entity using the Platform.</li>
            <li>
              "Group Administrator" or "Admin" refers to the person responsible for managing a
              group within the Platform.
            </li>
            <li>"Member" refers to any participant within a group.</li>
            <li>
              "Content" includes all uploaded, entered, generated, calculated, or displayed
              information.
            </li>
            <li>
              "Services" means the software tools, reporting features, record keeping systems,
              dashboards, and functionalities provided through the Platform.
            </li>
          </Box>

          {/* Section 3 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            3. Nature of the Platform
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            3.1 Record-Keeping Platform Only
          </Typography>
          <Typography paragraph>
            UTrust is strictly a software and digital record-keeping platform designed to assist
            groups, communities, families, partnerships, investment clubs, savings groups, and
            collaborative entities in recording and monitoring activities.
          </Typography>
          <Typography paragraph>
            The Platform is intended for administrative, reporting, transparency, and collaboration
            purposes only.
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            3.2 UTrust is NOT:
          </Typography>
          <Typography paragraph>UTrust is not:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>A bank</li>
            <li>A deposit-taking institution</li>
            <li>A managed investment scheme</li>
            <li>A financial product issuer</li>
            <li>A trustee company</li>
            <li>A custodian</li>
            <li>A licensed financial services provider</li>
            <li>A financial adviser</li>
            <li>A legal adviser</li>
            <li>A tax adviser</li>
            <li>An accounting firm</li>
            <li>A cryptocurrency exchange</li>
            <li>A crowd-funding operator</li>
          </Box>
          <Typography paragraph>
            Nothing within the Platform constitutes financial advice under the:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Corporations Act 2001 (Cth)</li>
            <li>Australian Securities and Investments Commission Act 2001 (Cth)</li>
            <li>Any applicable Australian financial services legislation</li>
          </Box>
          <Typography paragraph>
            Users acknowledge that all financial, investment, taxation, and legal decisions remain
            their sole responsibility.
          </Typography>

          {/* Section 4 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            4. User Responsibility
          </Typography>
          <Typography paragraph>Users are solely responsible for:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>The legality of their activities</li>
            <li>Compliance with Australian laws</li>
            <li>Accuracy of uploaded information</li>
            <li>Verifying records and calculations</li>
            <li>Maintaining supporting documentation</li>
            <li>Managing tax obligations</li>
            <li>Obtaining professional advice where required</li>
          </Box>
          <Typography paragraph>
            Users must not rely solely on information generated by the Platform for legal,
            financial, accounting, or investment decisions.
          </Typography>

          {/* Section 5 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            5. Group Administrator Responsibilities
          </Typography>
          <Typography paragraph>The Group Administrator is solely responsible for:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Approving or rejecting transactions</li>
            <li>Recording contributions</li>
            <li>Entering asset valuations</li>
            <li>Managing unit allocations</li>
            <li>Recording ownership interests</li>
            <li>Approving withdrawals</li>
            <li>Updating member records</li>
            <li>Reviewing calculations</li>
            <li>Managing access permissions</li>
            <li>Confirming portfolio balances</li>
            <li>Ensuring group transparency and governance</li>
          </Box>
          <Typography paragraph>
            The Platform performs calculations automatically based on information entered, approved,
            uploaded, or modified by users and administrators.
          </Typography>
          <Typography paragraph>
            UTrust does not independently verify the accuracy, legality, validity, existence,
            ownership, or legitimacy of any data entered into the Platform.
          </Typography>

          {/* Section 6 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            6. Accuracy of Data and Calculations
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            6.1 User-Controlled Inputs
          </Typography>
          <Typography paragraph>
            All calculations, reports, dashboards, balances, portfolio values, ownership
            percentages, and analytics generated by the Platform rely entirely on user-provided
            data.
          </Typography>
          <Typography paragraph>
            UTrust does not guarantee the accuracy, completeness, or reliability of any information
            displayed.
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            6.2 No Verification Obligation
          </Typography>
          <Typography paragraph>
            To the maximum extent permitted by law, UTrust has no obligation to:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Audit transactions</li>
            <li>Verify contributions</li>
            <li>Confirm asset ownership</li>
            <li>Validate valuations</li>
            <li>Investigate member disputes</li>
            <li>Reconcile external bank balances</li>
            <li>Confirm investment performance</li>
            <li>Detect fraud or misconduct</li>
          </Box>

          <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            6.3 Sole Responsibility for Misleading Data
          </Typography>
          <Typography paragraph>
            The Group Administrator and relevant users remain fully and solely responsible for:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Incorrect calculations resulting from inaccurate inputs</li>
            <li>Misleading financial information</li>
            <li>False or manipulated records</li>
            <li>Incorrect ownership records</li>
            <li>Improper unit allocations</li>
            <li>Fraudulent reporting</li>
            <li>Inaccurate distributions</li>
            <li>Taxation inaccuracies</li>
            <li>Any financial loss or dispute arising from user-entered data</li>
          </Box>
          <Typography paragraph>
            UTrust shall not be liable for any loss, damage, dispute, claim, or liability arising
            from inaccurate, misleading, incomplete, or manipulated information entered into the
            Platform.
          </Typography>

          {/* Section 7 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            7. No Fiduciary Relationship
          </Typography>
          <Typography paragraph>Nothing in these Terms creates:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>A fiduciary relationship</li>
            <li>A trustee relationship</li>
            <li>A partnership</li>
            <li>A joint venture</li>
            <li>An agency relationship</li>
            <li>A financial services relationship</li>
          </Box>
          <Typography paragraph>between UTrust and any user.</Typography>
          <Typography paragraph>
            Users remain solely responsible for managing their own affairs.
          </Typography>

          {/* Section 8 */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            8. Financial Services &amp; Regulatory Compliance
          </Typography>
          <Typography paragraph>
            Users acknowledge that they are responsible for ensuring compliance with:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
            <li>Corporations Act 2001 (Cth)</li>
            <li>Australian Consumer Law</li>
            <li>Anti-Money Laundering and Counter-Terrorism Financing Act 2006 (Cth)</li>
            <li>Privacy Act 1988 (Cth)</li>
            <li>Taxation Administration Act 1953 (Cth)</li>
            <li>
              Any cooperative, association, trust, partnership, or investment-related laws
              applicable in their jurisdiction
            </li>
          </Box>
          <Typography paragraph>
            If a group's activities require licensing, registration, disclosure obligations, or
            regulatory approval under Australian law, users are solely responsible for obtaining
            such approvals.
          </Typography>

          {/* Contact */}
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            Contact Us
          </Typography>
          <Typography paragraph>
            For any questions regarding these Terms and Conditions, please contact us at:
            <br />
            <strong>Email:</strong>{' '}
            <Link href="mailto:family.mwebeyatrust@gmail.com" sx={{ color: '#007f8b' }}>
              family.mwebeyatrust@gmail.com
            </Link>
            <br />
            <strong>Website:</strong>{' '}
            <Link
              href="https://www.ubumwetrust.com"
              target="_blank"
              rel="noopener"
              sx={{ color: '#007f8b' }}
            >
              https://www.ubumwetrust.com
            </Link>
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ color: '#007f8b', mt: 4, mb: 2, fontWeight: 'bold' }}
          >
            Changes to These Terms
          </Typography>
          <Typography paragraph>
            We may update these Terms and Conditions from time to time. Changes will be posted on
            this page with an updated effective date. Continued use of the Platform after changes
            constitutes acceptance of the updated Terms.
          </Typography>
        </Paper>
      </Container>

      <Box component="footer" sx={{ textAlign: 'center', p: 2, color: '#666', fontSize: '13px' }}>
        © {new Date().getFullYear()} UTrust Platform. All rights reserved.
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
